import { Icon } from "@iconify/react";
import { Colors } from "../../utils/colors";
import PopupModalContainer, {
  PopupModalContainerWidth,
} from "../Containers/PopupModalContainer";
import RectangleButton from "../Buttons/RectangleButton";
import { useCallback, useState } from "react";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import CustomTextField from "../CustomTextField";
import { useDispatch, useSelector } from "react-redux";
import {
  getAccountState,
  logoutAllUsers,
  logoutSingleUser,
} from "../../redux/slices/accountSlice";
import { API } from "../../API";
import AlertContainer from "../Containers/AlertContainer";
import ConfirmDeleteModal from "../Containers/ConfirmPopups/ConfirmDeleteModal";
import { useNavigate } from "react-router-dom";
import { hasSubscription } from "@markit/common.utils";
import { useMultiAccountManager } from "../../hooks/useMultiAccountManager";

type DeleteAccountModalProps = {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
};

const DeleteAccountModal = (props: DeleteAccountModalProps) => {
  const { isVisible, setIsVisible } = props;
  const { accountData, additionalAccountData } =
    useSelector(getAccountState).account;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [alertText, setAlertText] = useState<{
    header: string;
    subtext: string;
  }>({ header: "", subtext: "" });
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const { switchToAdditionalAccount } = useMultiAccountManager();

  const deleteAccount = useCallback(async () => {
    setLoading(true);
    const { success } = await API.profile.deleteUserAccount({
      uid: accountData.uid,
    });
    if (success) {
      if (additionalAccountData.length > 0) {
        await switchToAdditionalAccount(additionalAccountData[0].uid);
        dispatch(logoutSingleUser(accountData.uid, true));
      } else {
        dispatch(logoutAllUsers(true));
        navigate("/");
      }
    } else {
      setAlertText({
        header: "There was an error deleting your account",
        subtext: "Please try again later.",
      });
    }
    setLoading(false);
  }, [
    accountData.uid,
    additionalAccountData,
    dispatch,
    navigate,
    switchToAdditionalAccount,
  ]);

  const showConfirmDialog = () => {
    if (accountData.username !== username) {
      setAlertText({
        header: "Please try again",
        subtext: "The username entered does not match your account username.",
      });
      return;
    }
    setConfirmModal(true);
  };

  return (
    <>
      {isVisible ? (
        hasSubscription(accountData) ? (
          <AlertContainer
            headerComp={
              "Please reach out to support if you'd like to delete your account."
            }
            closeModal={() => setIsVisible(false)}
            hideModal={false}
          />
        ) : (
          <PopupModalContainer
            headerComp={
              <div className="ColumnNormal" style={{ gap: 10 }}>
                <Icon icon="ion:person" height={35} />
                <span className="sectionTitle">Delete Account</span>
              </div>
            }
            valueComp={
              <div
                className="ColumnNormal"
                style={{ gap: 24, paddingBottom: 14 }}
              >
                <div className="ColumnNormal" style={{ gap: 10 }}>
                  <span className="bodyMedium">
                    Enter username to delete account
                  </span>
                  <div className="ColumnNormal" style={{ gap: 4 }}>
                    <CustomTextField
                      value={username}
                      placeholder="Type username here"
                      inputMode="text"
                      borderRadius={12}
                      backgroundColor={Colors.WHITE1}
                      onChange={(change: any) => {
                        setUsername(change.target.value);
                      }}
                      altMarginBottom={0}
                    />
                  </div>
                </div>
              </div>
            }
            fixedBottomContent={
              <div className="ColumnNormal" style={{ gap: 10 }}>
                <HorizontalDivider />
                <div
                  className="AlignedRow"
                  style={{ gap: 10, paddingInline: 14 }}
                >
                  <RectangleButton
                    buttonLabel={"Cancel"}
                    onPress={() => setIsVisible(false)}
                    altColor={Colors.WHITE1}
                    altTextColor={Colors.BLACK}
                    altPaddingHorz={12}
                    altPaddingVert={14}
                    altBorderRadius={12}
                    containerStyles={{
                      border: `0.5px solid ${Colors.GRAY2}`,
                    }}
                  />
                  <RectangleButton
                    buttonLabel={"Delete Account"}
                    onPress={showConfirmDialog}
                    disabled={username === ""}
                    loading={loading}
                    altColor={Colors.RED3}
                    altTextColor={Colors.WHITE}
                    altPaddingHorz={12}
                    altPaddingVert={14}
                    altBorderRadius={12}
                  />
                </div>
              </div>
            }
            noExit
            closeOnOutsidePress={!confirmModal && !alertText.header}
            closeModal={() => setIsVisible(false)}
            modalContentWidth={PopupModalContainerWidth.SMALL}
          />
        )
      ) : null}
      <AlertContainer
        headerComp={alertText.header}
        subHeaderComp={alertText.subtext}
        closeModal={() => setAlertText({ header: "", subtext: "" })}
        hideModal={alertText.header === "" && alertText.subtext === ""}
      />
      <ConfirmDeleteModal
        heading={"Are you sure you want to delete your account?"}
        subtext="You will not be able to undo this."
        deleteButtonText="Delete Account"
        hideModal={!confirmModal}
        setIsVisible={setConfirmModal}
        deleteOnPress={deleteAccount}
      />
    </>
  );
};

export default DeleteAccountModal;
